import * as ft from 'funtypes'
import { IsoDateTime, Latitude, Longitude, objectWith } from '../validation.js'
import { ConnectorStandard } from './connectors.js'
import { AuthMethod, PaymentMethod } from './methods.js'
import { PublishedOperatorSlug } from './operators.js'

/**
 * The canonical domain model.
 *
 * Deliberately decoupled from EIPA: field names, units and enumerations are
 * ours, so a second registry (Open Charge Map, an operator's own API, a manual
 * override) can feed the same model. Nothing here may reference an EIPA
 * dictionary id.
 */

// ---------------------------------------------------------------------------
// Closed unions
// ---------------------------------------------------------------------------

/**
 * Which upstream registry a record came from.
 *
 * `other` is the community dataset this site credits without naming - the same
 * source the about page calls "other sources" and `VoltageSource` calls
 * `ge-declared`. It is the only value that is not a name, and deliberately: a
 * site standing on it is published under an attribution that has no homepage to
 * link to, and putting the project's name in the artifact would undo that in the
 * one place a reader can read it.
 */
export const SourceId = ft.Union(
  ft.Literal('eipa'),
  ft.Literal('ocm'),
  ft.Literal('other'),
  ft.Literal('manual'),
)
export type SourceId = ft.Static<typeof SourceId>

export const StationType = ft.Union(
  ft.Literal('electric'),
  ft.Literal('gas'),
  ft.Literal('hydrogen'),
)
export type StationType = ft.Static<typeof StationType>

export const CurrentType = ft.Union(ft.Literal('AC'), ft.Literal('DC'), ft.Literal('unknown'))
export type CurrentType = ft.Static<typeof CurrentType>

/** IEC 61851 charging modes, named as EIPA names them. */
export const ChargingMode = ft.Union(
  ft.Literal('Mode1-AC-1p'),
  ft.Literal('Mode1-AC-3p'),
  ft.Literal('Mode2-AC-1p'),
  ft.Literal('Mode2-AC-3p'),
  ft.Literal('Mode3-AC-1p'),
  ft.Literal('Mode3-AC-3p'),
  ft.Literal('Mode4-DC'),
  ft.Literal('unknown'),
)
export type ChargingMode = ft.Static<typeof ChargingMode>

/**
 * Where a {@link VoltageInfo} came from.
 *
 * Not in order of trust, because the two declared sources do not sit in a fixed
 * order: a `ge-declared` figure joined by EVSE id outranks `ocm-declared`, and one
 * joined by address does not. {@link Confidence} carries that difference, and
 * `packages/shared/src/ge/enrich.ts` documents the full precedence.
 */
export const VoltageSource = ft.Union(
  /** GoingElectric published volts/amps for this EVSE id or its address. */
  ft.Literal('ge-declared'),
  /** Open Charge Map published Voltage/Amps for the matched connector. */
  ft.Literal('ocm-declared'),
  /** Derived from the IEC 61851 charging mode. */
  ft.Literal('derived-mode'),
  /** Derived from the connector standard alone. */
  ft.Literal('derived-connector'),
)
export type VoltageSource = ft.Static<typeof VoltageSource>

export const Confidence = ft.Union(ft.Literal('high'), ft.Literal('medium'), ft.Literal('low'))
export type Confidence = ft.Static<typeof Confidence>

/**
 * Coarse voltage buckets, used for filtering and for the `v` property of
 * `stations.geojson`. Boundaries are fixed here so every implementation of
 * `voltageClass()` agrees:
 *
 * - `ac-230`  AC, nominal <= 300 V
 * - `ac-400`  AC, nominal  > 300 V
 * - `dc-400`  DC, nominal <= 500 V
 * - `dc-800`  DC, nominal  > 500 V
 * - `unknown` no nominal voltage, or an unknown current type
 */
export const VoltageClass = ft.Union(
  ft.Literal('ac-230'),
  ft.Literal('ac-400'),
  ft.Literal('dc-400'),
  ft.Literal('dc-800'),
  ft.Literal('unknown'),
)
export type VoltageClass = ft.Static<typeof VoltageClass>

/** 1 = Monday ... 7 = Sunday, matching `dictionary.weekday`. */
export const Weekday = ft.Union(
  ft.Literal(1),
  ft.Literal(2),
  ft.Literal(3),
  ft.Literal(4),
  ft.Literal(5),
  ft.Literal(6),
  ft.Literal(7),
)
export type Weekday = ft.Static<typeof Weekday>

// ---------------------------------------------------------------------------
// Leaves
// ---------------------------------------------------------------------------

/** The numbers a source can put on a connector. Every one of them optional. */
const VOLTAGE_FIGURES = {
  nominalV: ft.Number,
  minV: ft.Number,
  maxV: ft.Number,
  amps: ft.Number,
  /** 1 or 3 for AC; absent for DC. */
  phases: ft.Number,
}

/**
 * One source's reading of a connector's electrics.
 *
 * Identical to {@link VoltageInfo} minus the alternatives, because an
 * alternative cannot itself be contradicted: the disagreement is a property of
 * the connector, and one flat list of readings says everything there is to say
 * about it.
 */
export const VoltageReading = objectWith(
  {
    source: VoltageSource,
    confidence: Confidence,
  },
  VOLTAGE_FIGURES,
)
export type VoltageReading = ft.Static<typeof VoltageReading>

export const VoltageInfo = objectWith(
  {
    source: VoltageSource,
    confidence: Confidence,
  },
  {
    ...VOLTAGE_FIGURES,
    /**
     * Readings from other sources that contradict this one, if any.
     *
     * Present only where two sources that both *published* a figure disagree
     * about the same connector - never where a declaration simply replaced an
     * inference, which is the pipeline working rather than the data conflicting.
     * The published figure stays the one the precedence picked; these ride
     * beside it so the panel can show the reader both numbers and how firmly
     * each is tied to this connector, instead of quietly dropping the loser.
     *
     * Absent, rather than empty, when nothing disagrees.
     */
    alternatives: ft.Array(VoltageReading),
  },
)
export type VoltageInfo = ft.Static<typeof VoltageInfo>

/** The two sources that publish electrics of their own, as opposed to inference. */
export const DeclaringSource = ft.Union(ft.Literal('ge-declared'), ft.Literal('ocm-declared'))
export type DeclaringSource = ft.Static<typeof DeclaringSource>

/**
 * A power rating somebody other than the registry published for this connector.
 *
 * EIPA stays authoritative for `powerKw` - the registry is the legal record of
 * what is installed - so this never replaces it. It is here because a matched
 * source declaring 140 kW against the registry's 150 kW is exactly the kind of
 * disagreement a driver is entitled to see rather than have resolved for them.
 */
export const PowerClaim = ft.Object({
  source: DeclaringSource,
  /** How firmly the claim is tied to *this* connector, not how right it is. */
  confidence: Confidence,
  powerKw: ft.Number,
})
export type PowerClaim = ft.Static<typeof PowerClaim>

export const Connector = objectWith(
  {
    /** One physical connector may expose several standards (e.g. a CCS2 gun). */
    interfaces: ft.Array(ConnectorStandard),
    cableAttached: ft.Boolean,
    powerKw: ft.Number,
  },
  {
    /** Absent when nothing could be established at all. */
    voltage: VoltageInfo,
    /** Present only when another source contradicts {@link powerKw}. */
    disputedPower: PowerClaim,
  },
)
export type Connector = ft.Static<typeof Connector>

export const ChargePoint = ft.Object({
  id: ft.String,
  /** The operator-assigned EIPA point code, e.g. `PL-S77-E12345ABC`. */
  code: ft.String,
  maxPowerKw: ft.Number,
  currentType: CurrentType,
  chargingModes: ft.Array(ChargingMode),
  connectors: ft.Array(Connector),
})
export type ChargePoint = ft.Static<typeof ChargePoint>

export const Station = objectWith(
  {
    id: ft.String,
    type: StationType,
    /** Falls back to the parent site when the station declares no coordinates. */
    lat: Latitude,
    lon: Longitude,
    authMethods: ft.Array(AuthMethod),
    paymentMethods: ft.Array(PaymentMethod),
    points: ft.Array(ChargePoint),
  },
  {},
)
export type Station = ft.Static<typeof Station>

export const SiteAddress = objectWith(
  {
    street: ft.String,
    houseNumber: ft.String,
    postalCode: ft.String,
    city: ft.String,
  },
  {
    houseNumberAddition: ft.String,
  },
)
export type SiteAddress = ft.Static<typeof SiteAddress>

/** Polish administrative hierarchy: gmina, powiat, wojewodztwo. */
export const AdminArea = ft.Object({
  city: ft.String,
  community: ft.String,
  district: ft.String,
  province: ft.String,
})
export type AdminArea = ft.Static<typeof AdminArea>

/** One weekly opening window. `08:00`-style local times, no timezone. */
export const OpeningHours = ft.Object({
  weekday: Weekday,
  fromTime: ft.String,
  toTime: ft.String,
})
export type OpeningHours = ft.Static<typeof OpeningHours>

// ---------------------------------------------------------------------------
// Site
// ---------------------------------------------------------------------------

/**
 * Why a site is shown with a warning rather than as settled fact.
 *
 * Every reason comes from the GoingElectric stage, which is the only thing in the
 * pipeline that reads a second source at all. Two of them are about a site EIPA
 * lists, where what is in doubt is whether the charger is really at the address
 * the registry gives; the third is about a site EIPA does not list, where what is
 * in doubt is the record itself:
 *
 *  - `address-contested` - several different stations are listed at this address
 *    and nothing can say which of them this record is.
 *  - `listing-retired` - a station listed at this address was reported as no
 *    longer being there. The strongest doubt on offer, and the only one that comes
 *    from somebody standing in front of it.
 *  - `unlisted` - the site is not in the registry at all, so the only thing that
 *    says it exists is the community dataset it came from. Unlike the other two
 *    it is a doubt about the *whole record* rather than about its address, and it
 *    is the reason every site published under `source: 'other'` carries.
 *
 * A site with no doubt carries no field at all, so `unconfirmed` being present is
 * itself the flag and there is no `status: 'confirmed'` to forget to check.
 *
 * A further reason is a compile error wherever it has to be spoken for, which is
 * the point of a union rather than a string: the copy in
 * `apps/web/src/i18n/strings.ts` is keyed by it, and the panel's explanation
 * switch ends in `assertNever`. Which of the two contested reasons a listed site
 * gets is decided by a separate union - `GeContestedReason` - mapped onto this
 * one by an equally exhaustive switch in `ge/enrich.ts`; `unlisted` has no such
 * mapping because it is not a finding about an address, it is what standing a
 * site up on one source means.
 */
export const UnconfirmedReason = ft.Union(
  ft.Literal('address-contested'),
  ft.Literal('listing-retired'),
  ft.Literal('unlisted'),
)
export type UnconfirmedReason = ft.Static<typeof UnconfirmedReason>

export const SiteUnconfirmed = ft.Object({
  reason: UnconfirmedReason,
  /** How many other stations are listed at this address. 0 where the reason is not a count. */
  otherListings: ft.Number,
})
export type SiteUnconfirmed = ft.Static<typeof SiteUnconfirmed>

export const Site = objectWith(
  {
    id: ft.String,
    source: SourceId,
    /** Canonical operator slug from the registry, or `other`. */
    operatorSlug: PublishedOperatorSlug,
    name: ft.String,
    lat: Latitude,
    lon: Longitude,
    address: SiteAddress,
    admin: AdminArea,
    /** Free-text access directions from the operator. */
    accessibility: ft.String,
    openingHours: ft.Array(OpeningHours),
    stations: ft.Array(Station),
  },
  {
    /**
     * Newest upstream timestamp across the site's rows. Drives `upd`.
     *
     * Optional because not every source stamps its rows. EIPA does, on all of
     * them; the community dataset behind `source: 'other'` stamps nothing, and
     * the run's own clock is not an answer - it would print today's date under
     * "last updated" for a record scraped months ago, which is the one reading
     * of this field that is worse than its absence.
     */
    updatedAt: IsoDateTime,
    /** Metres above sea level. Absent for sources that do not publish it. */
    elevation: ft.Number,
    /** Present only when something casts doubt on the site. See {@link SiteUnconfirmed}. */
    unconfirmed: SiteUnconfirmed,
    /**
     * The operator name as the source published it, kept only where
     * `operatorSlug` is `other`.
     *
     * The fold onto a slug is lossy by design - it is what turns forty spellings
     * of one brand into one filter row - but for the long tail it was throwing
     * away the only name the network has. Keeping it here is what lets the map
     * offer "ThreeForce" to somebody searching for it, rather than answering
     * with the bucket the fold put it in.
     */
    operatorName: ft.String,
  },
)
export type Site = ft.Static<typeof Site>
